import { DateTime } from 'luxon';
import * as React from 'react';
import { ReactNode, useMemo } from 'react';
import '../../scss/00_styleguide/_faux-input.scss';
import { mergeTests } from '../../test-utilities/mergeTests';
import { ICalendarValue } from '../Calendar';
import { extractFormCellProps, FormCell, IFormCellProps } from '../FormCell';
import { NumberInputStyled } from '../NumberInput';
import { CalendarPosition, DateInputContainer } from './DateInput.styles';
import { DateInputCalendarPopup } from './DateInputCalendarPopup';
import { DateFormat, getDateString } from '../../utilities/getDateString';

export interface IDateInputValue {
  // value in ISO format or the actual date if available
  value: string | null;
  // value as a Date js object
  dateValue: Date | null;
  error: string | null;
  name: string | undefined;
}

export interface IDateInputProps extends IFormCellProps {
  /** Set to true to trigger autofocus of the date input */
  autoFocus?: boolean;
  /** Maximum date that the user can select. Date must be in ISO8601 Date format: YYYY-MM-DD.  */
  maxDate?: string;
  /** Minimum date that the user can select. Date must be in ISO8601 Date format: YYYY-MM-DD.  */
  minDate?: string;
  /** The date value attribute of input. Date must be in ISO8601 Date format: YYYY-MM-DD. */
  value: string | null;
  /** Text that will appear in an empty input. */
  placeholder?: string;
  /** Name of the submitted form parameter. */
  name?: string;
  /** This callback exposes the input reference / DOM node to parent components. */
  getInputRef?: (element: HTMLInputElement | null) => void;
  /** A callback triggered when component is focused. */
  onFocus?: (event: React.FocusEvent<HTMLInputElement>, value?: IDateInputValue) => void;
  /** A callback triggered when focus is removed. */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>, value?: IDateInputValue) => void;
  /** A callback fired when the value prop changes. */
  onChange: (value: IDateInputValue, e?: React.ChangeEvent<HTMLInputElement> | React.MouseEvent) => void;
  /** Switches the input to read only which prevents editing the contents. */
  readOnly?: boolean;
  /** Highlight given date in the calendar picker. */
  highlightDate?: 'today' | string;
  /** Sets the zIndex of the substrate and calendar modal. @deprecated because portal usage eliminates the need for this
   * by allowing the DOM order to determine how far forward something should be.
   */
  zIndex?: number;
  attachPortalInsideContainter?: boolean;
}

const CALENDAR_WIDTH = 316;
const CALENDAR_HEIGHT = 380;

interface IGetErrorMessageProps {
  label?: ReactNode;
  isRequired?: boolean;
  minDate?: string;
  maxDate?: string;
  errorText?: ReactNode;
}

export const getErrorMessageBase =
  ({ label, isRequired, minDate, maxDate, errorText }: IGetErrorMessageProps) =>
  (formattedValue: string): string | null => {
    let errorMessage: string | null = null;
    if (formattedValue.length === 10 && formattedValue.indexOf(' ') === -1) {
      errorMessage = DateTime.fromFormat(formattedValue, DateFormat.short).isValid ? null : 'Invalid Date';
    }
    if (minDate && !errorMessage) {
      if (DateTime.fromFormat(formattedValue, DateFormat.short).toISODate() < minDate) {
        errorMessage = `${label || ''} cannot be less than ${getDateString(DateFormat.short, minDate)}`.trim();
      }
    }
    if (maxDate && !errorMessage) {
      if (DateTime.fromFormat(formattedValue, DateFormat.short).toISODate() > maxDate) {
        errorMessage = `${label || ''} cannot be more than ${getDateString(DateFormat.short, maxDate)}`.trim();
      }
    }
    if (!formattedValue && !errorText && isRequired) {
      errorMessage = `${label || ''} cannot be blank`.trim();
    }
    return errorMessage;
  };

export const getValueBase =
  (getErrorMessage: (x: string) => string | null, name?: string) =>
  (value: string): IDateInputValue => {
    const toISO: string | null = DateTime.fromFormat(value, DateFormat.short).isValid
      ? DateTime.fromFormat(value, DateFormat.short).toISODate()
      : null;
    const errorMessage: string | null = getErrorMessage(value);
    const dateFromFormat = DateTime.fromFormat(value, DateFormat.short);
    const date: Date | null = dateFromFormat.isValid ? dateFromFormat.toJSDate() : null;
    const [month, day, year] = value ? value.split('/') : ['  ', '  ', '    '];
    let rawValue: string | null = [year, month, day].join('-');
    if (year === '    ' && month === '  ' && day === '  ') {
      rawValue = null;
    }
    let valOut = rawValue;
    if (toISO) {
      // If the value is a valid ISO Date, output that. Otherwise, output the raw text
      valOut = toISO;
    }
    return {
      dateValue: date,
      error: errorMessage,
      name: undefined,
      value: valOut,
    };
  };

export const onChangeBase =
  (getValue: (x: string) => IDateInputValue, setInputValue: (x: string) => void) =>
  (onChange: (value: IDateInputValue, e?: React.ChangeEvent<HTMLInputElement> | React.MouseEvent) => void) =>
  (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputDate = getValue(e.target.value);
    setInputValue(e.target.value);
    onChange(
      {
        dateValue: inputDate.dateValue,
        error: inputDate.error,
        name: e.target.name,
        value: inputDate.value,
      },
      e
    );
  };

/**
 * A date input is a non-text input form element. You can select a single date from a popup calendar.
 * More info in {@link Calendar}
 */
export const DateInput: React.FC<IDateInputProps> = ({
  errorText = null,
  value = '',
  isRequired = false,
  placeholder = 'MM/DD/YYYY',
  ...rest
}) => {
  const props = useMemo(
    () => ({ errorText, value, isRequired, placeholder, ...rest }),
    [errorText, value, isRequired, placeholder, rest]
  );
  const { formCellProps, inputProps } = extractFormCellProps(props);
  const {
    autoFocus,
    value: valueFromExtraction,
    readOnly,
    placeholder: placeholderFromExtraction,
    name,
    zIndex,
    attachPortalInsideContainter,
    'data-test-id': dataTestId,
  } = inputProps;
  const dataTestClass = props['data-test-class'] || 'date-input';

  const [inputValue, setInputValue] = React.useState(valueFromExtraction);

  const dateISO = React.useMemo(() => {
    return !!inputValue && DateTime.fromFormat(inputValue, DateFormat.short).isValid
      ? DateTime.fromFormat(inputValue, DateFormat.short).toISODate()
      : null;
  }, [inputValue]);

  const [showCalendar, setShowCalendar] = React.useState(false);
  const [calendarPosition, setCalendarPosition] = React.useState(CalendarPosition.left);
  const [calendarAnchor, setCalendarAnchor] = React.useState({ x: 0, y: 0 });

  let wrappingDiv = React.createRef<HTMLDivElement>();

  React.useEffect(() => {
    const newDateTime = DateTime.fromISO(valueFromExtraction || '');
    const [year, month, day] = valueFromExtraction ? valueFromExtraction.split('-') : ['  ', '  ', '  '];
    const rawDateFormatted = [month, day, year].join('/');
    setInputValue(newDateTime.isValid ? newDateTime.toFormat(DateFormat.short) : rawDateFormatted);
  }, [valueFromExtraction]);

  const toggleCalendar = () => {
    if (readOnly) {
      return;
    }
    let newCalendarPosition = CalendarPosition.left;
    if (!showCalendar) {
      if (
        wrappingDiv.current &&
        window.innerWidth - wrappingDiv.current.offsetLeft - wrappingDiv.current.offsetWidth < CALENDAR_WIDTH
      ) {
        newCalendarPosition = CalendarPosition.right;
      }
      if (
        wrappingDiv.current &&
        window.innerHeight - wrappingDiv.current.getBoundingClientRect().top < CALENDAR_HEIGHT &&
        wrappingDiv.current.getBoundingClientRect().top > CALENDAR_HEIGHT
      ) {
        newCalendarPosition = CalendarPosition.top;
      }
    }
    setShowCalendar(!showCalendar);
    setCalendarPosition(newCalendarPosition);
    if (wrappingDiv.current) {
      const wrappingDivRect = wrappingDiv.current.getBoundingClientRect();
      setCalendarAnchor({
        x: newCalendarPosition === CalendarPosition.right ? wrappingDivRect.right : wrappingDivRect.left,
        y: newCalendarPosition === CalendarPosition.top ? wrappingDivRect.top : wrappingDivRect.bottom,
      });
    } else setCalendarAnchor({ x: 0, y: 0 });
  };

  const onClickHandler = (e: React.MouseEvent<HTMLButtonElement>) =>
    setCalendarValue({ error: null, event: e, value: null });
  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => props.onBlur && props.onBlur(e, getValue(e.target.value));
  const onFocus = (e: React.FocusEvent<HTMLInputElement>) =>
    props.onFocus && props.onFocus(e, getValue(inputValue || ''));
  const getErrorMessage = getErrorMessageBase(props);
  const getValue = getValueBase(getErrorMessage, name);
  const dateInputOnchange = onChangeBase(getValue, setInputValue)(props.onChange);

  const setCalendarValue = ({ event, value }: ICalendarValue) => {
    const inputValue: string = value ? getDateString(DateFormat.short, value) : '';
    const dateValue: Date | null = value ? DateTime.fromISO(value).toJSDate() : null;
    setInputValue(inputValue);
    props.onChange({ dateValue, error: null, name, value }, event);
    toggleCalendar();
  };
  // const themeContext = useContext(ThemeContext);

  return (
    <div className={formCellProps.className} ref={wrappingDiv}>
      <FormCell {...formCellProps} data-test-id={dataTestId} data-test-class={dataTestClass}>
        <DateInputContainer>
          <NumberInputStyled
            autoFocus={autoFocus}
            showErrorHighlight={!!formCellProps.errorText}
            value={inputValue}
            placeholder={placeholderFromExtraction}
            name={name}
            displayType={'input'}
            onBlur={onBlur}
            onFocus={onFocus}
            onChange={dateInputOnchange}
            onClick={toggleCalendar}
            isOpen={showCalendar}
            thousandSeparator={''}
            format={'##/##/####'}
            decimalScale={0}
            readOnly={readOnly}
            data-test-id={mergeTests(dataTestId, 'date-input')}
            data-test-class={dataTestClass}
          />

          {showCalendar ? (
            <DateInputCalendarPopup
              attachPortalInsideContainer={!!attachPortalInsideContainter}
              calendarAnchor={calendarAnchor}
              calendarPosition={calendarPosition}
              zIndex={zIndex}
              toggleCalendar={toggleCalendar}
              setCalendarValue={setCalendarValue}
              onClickHandler={onClickHandler}
              dateISO={dateISO}
              maxDate={props.maxDate}
              minDate={props.minDate}
              highlightDate={props.highlightDate}
              calendarContent={props.children}
            />
          ) : null}
        </DateInputContainer>
      </FormCell>
    </div>
  );
};
